import React from "react";
import { CheckOutlined } from "@ant-design/icons";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";
import InfoIcon from "../InfoIcon";
import "../../../renderer/container_effects.css";

const EFFECT_OPTIONS = [
  {
    value: "none",
    label: "None",
    description: "No effect overlay",
    category: "basic",
  },
  {
    value: "glass",
    label: "Glass",
    description: "Frosted glassmorphism with backdrop blur",
    category: "surface",
  },
  {
    value: "frost",
    label: "Frost",
    description: "Heavy frosted overlay with soft saturation",
    category: "surface",
  },
  {
    value: "emboss",
    label: "Emboss",
    description: "3D raised surface with light and shadow",
    category: "depth",
  },
  {
    value: "etch",
    label: "Etch",
    description: "Subtle engraved letterpress depth",
    category: "depth",
  },
  {
    value: "ridge",
    label: "Ridge",
    description: "Layered edge depth with highlights",
    category: "depth",
  },
  {
    value: "holographic",
    label: "Holo",
    description: "Prismatic rainbow shimmer",
    category: "color",
  },
  {
    value: "prism",
    label: "Prism",
    description: "Conic rainbow light refraction",
    category: "color",
    animatable: true,
  },
  {
    value: "mesh",
    label: "Mesh",
    description: "Vibrant gradient mesh overlay",
    category: "color",
    animatable: true,
  },
  {
    value: "silk",
    label: "Silk",
    description: "Smooth satin sheen reflection",
    category: "texture",
  },
  {
    value: "noise",
    label: "Noise",
    description: "Film grain texture for analog warmth",
    category: "texture",
  },
  {
    value: "crackle",
    label: "Crackle",
    description: "Gritty noise for a raw tactile feel",
    category: "texture",
  },
  {
    value: "scanlines",
    label: "Scanlines",
    description: "Retro CRT horizontal line pattern",
    category: "texture",
  },
  {
    value: "halftone",
    label: "Halftone",
    description: "Print-style Ben-Day dot pattern",
    category: "texture",
  },
  {
    value: "vignette",
    label: "Vignette",
    description: "Cinematic darkened edge fade",
    category: "depth",
  },
  {
    value: "shadow",
    label: "Shadow",
    description: "Partial shadow on chosen edges (top, bottom, left, right)",
    category: "depth",
  },
  {
    value: "sunken",
    label: "Sunken",
    description: "Recessed depth like sitting in a hole",
    category: "depth",
  },
  {
    value: "streak",
    label: "Streak",
    description: "Diagonal light flare sweep",
    category: "color",
    animatable: true,
  },
  {
    value: "glow",
    label: "Glow",
    description: "Soft luminous ambient aura",
    category: "color",
    animatable: true,
  },
  {
    value: "brushed",
    label: "Brushed",
    description: "Brushed metal directional grain",
    category: "texture",
  },
];

const CATEGORY_LABELS = {
  basic: "Basic",
  surface: "Glass & Frost",
  depth: "3D Depth",
  color: "Color Overlays",
  texture: "Textures",
};

const CATEGORY_ORDER = ["basic", "surface", "depth", "color", "texture"];

const EFFECT_CLASS_MAP = {
  glass: "ce-glass",
  frost: "ce-frost",
  crackle: "ce-crackle",
  emboss: "ce-emboss",
  etch: "ce-etch",
  holographic: "ce-holographic",
  noise: "ce-noise",
  prism: "ce-prism",
  ridge: "ce-ridge",
  silk: "ce-silk",
  mesh: "ce-mesh",
  vignette: "ce-vignette",
  scanlines: "ce-scanlines",
  halftone: "ce-halftone",
  streak: "ce-streak",
  sunken: "ce-sunken",
  glow: "ce-glow",
  brushed: "ce-brushed",
  shadow: "ce-shadow",
};

const BG_MAP = {
  glass: "bg-gradient-to-br from-sky-400/70 to-blue-500/70 dark:from-sky-600/50 dark:to-blue-700/50",
  frost: "bg-gradient-to-br from-indigo-300/70 to-blue-400/70 dark:from-indigo-500/50 dark:to-blue-600/50",
  crackle: "bg-gradient-to-br from-stone-500 to-stone-700 dark:from-stone-600 dark:to-stone-800",
  emboss: "bg-gradient-to-br from-gray-300 to-gray-500 dark:from-gray-500 dark:to-gray-700",
  etch: "bg-gradient-to-b from-slate-300 to-slate-400 dark:from-slate-500 dark:to-slate-600",
  holographic: "bg-gradient-to-br from-gray-800 to-gray-900 dark:from-gray-700 dark:to-gray-800",
  noise: "bg-gradient-to-br from-amber-100 to-orange-200 dark:from-amber-800/60 dark:to-orange-900/60",
  prism: "bg-gradient-to-br from-gray-800 to-gray-900 dark:from-gray-700 dark:to-gray-800",
  ridge: "bg-gradient-to-br from-slate-300 to-slate-500 dark:from-slate-500 dark:to-slate-700",
  silk: "bg-gradient-to-r from-gray-200 via-white to-gray-200 dark:from-gray-600 dark:via-gray-500 dark:to-gray-600",
  mesh: "bg-gradient-to-br from-gray-800 to-gray-900 dark:from-gray-700 dark:to-gray-800",
  vignette: "bg-gradient-to-br from-amber-200 to-orange-300 dark:from-amber-400/60 dark:to-orange-500/60",
  scanlines: "bg-gradient-to-br from-teal-300 to-cyan-400 dark:from-teal-500/60 dark:to-cyan-600/60",
  halftone: "bg-gradient-to-br from-rose-300 to-pink-400 dark:from-rose-500/60 dark:to-pink-600/60",
  streak: "bg-gradient-to-br from-gray-800 to-slate-900 dark:from-gray-700 dark:to-slate-800",
  sunken: "bg-gradient-to-br from-stone-300 to-stone-400 dark:from-stone-500 dark:to-stone-600",
  glow: "bg-gradient-to-br from-indigo-900 to-slate-900 dark:from-indigo-800 dark:to-slate-800",
  brushed: "bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 dark:from-gray-500 dark:via-gray-600 dark:to-gray-500",
  shadow: "bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700",
};

const EffectPreview = React.memo(({ effectValue, isSelected }) => {
  if (effectValue === "none") {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <svg
          className={`w-5 h-5 ${isSelected ? "text-white/70" : "text-gray-400 dark:text-dark-500"}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      </div>
    );
  }

  const effectClass = EFFECT_CLASS_MAP[effectValue] || "";
  const bgClass = BG_MAP[effectValue] || "bg-gray-300 dark:bg-dark-600";

  return (
    <div className="relative w-full h-full rounded overflow-hidden">
      <div className={`absolute inset-0 rounded ${bgClass}`} />
      <div
        className={`absolute inset-0 rounded ${effectClass}`}
        style={{ "--ce-intensity": 70, "--ce-blur": "8px" }}
      />
    </div>
  );
});
EffectPreview.displayName = "EffectPreview";

export default function ContainerEffectField({ label, help, value, onChange, infoText }) {
  const currentValue = value || "none";

  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const items = EFFECT_OPTIONS.filter((o) => o.category === cat);
    if (items.length > 0) acc.push({ category: cat, label: CATEGORY_LABELS[cat], items });
    return acc;
  }, []);

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? <span className="ml-2"><InfoIcon text={infoText} /></span> : null}
        </label>
      )}

      <div className={`border ${borders.default} ${surfaces.primary} rounded-xl overflow-hidden`}>
        {grouped.map(({ category, label: catLabel, items }) => (
          <div key={category}>
            {category !== "basic" && (
              <div className={`px-3 pt-2.5 pb-1 ${surfaces.secondary} border-t ${borders.default}`}>
                <span className={`${typography.caption} ${textColors.muted} font-semibold uppercase tracking-wider`}>
                  {catLabel}
                </span>
              </div>
            )}

            <div
              className="grid gap-1.5 p-2.5"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))" }}
            >
              {items.map((opt) => {
                const selected = currentValue === opt.value;

                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    className={`flex flex-col items-center p-1.5 rounded-lg border transition-all ${
                      selected
                        ? `${interactive.selectedBg} ${interactive.selectedBorder} shadow-sm`
                        : `${borders.default} ${surfaces.tertiary} ${surfaces.hover}`
                    }`}
                    title={opt.description}
                    aria-pressed={selected}
                  >
                    <div className="relative w-full aspect-square rounded overflow-hidden mb-1">
                      <EffectPreview effectValue={opt.value} isSelected={selected} />
                      {selected && (
                        <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-white/90 dark:bg-white/80 flex items-center justify-center shadow-sm">
                          <CheckOutlined style={{ fontSize: 8, color: "#2563eb" }} />
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-[10px] leading-tight font-medium text-center w-full truncate ${
                        selected ? "text-white" : textColors.primary
                      }`}
                    >
                      {opt.label}
                    </span>
                    {opt.animatable && (
                      <span
                        className={`mt-0.5 text-[8px] font-semibold leading-none px-1 py-px rounded ${
                          selected
                            ? "bg-white/20 text-white/80"
                            : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                        }`}
                      >
                        Anim
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {help && (
        <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
          {help}
        </div>
      )}
    </div>
  );
}
